import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Edit_Icon from "../../assets/icons/Edit.svg";
import { ReactComponent as UploadPictureIcon } from "../../assets/icons/Upload_Picture.svg";
import CheckMark from "../../assets/icons/CheckMark.svg";
import { Link } from "react-router-dom";
import Spinner from "../../components/Spinner";
import { toast } from "react-toastify";
import SidePanel from "../../components/SidePanel";

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // For immediate previewing

  // Function to upload image and update profile
  const uploadImageAndUpdateProfile = async (imageFile) => {
    if (!selectedImage) return; // Early exit if no image selected
    setLoading(true);

    // Step 1: Upload image to S3 and get URL
    const formData = new FormData();
    formData.append("image", selectedImage);

    try {
      const uploadResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/upload-s3`,
        {
          method: "POST",
          body: formData,
          // Add any other headers needed for authorization or content type
        }
      );
      const uploadData = await uploadResponse.json();

      if (uploadData.code === 200 && uploadData.data.detail) {
        // Step 2: Update customer profile with new image URL
        const profileUpdateResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/customer/update-customer`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "x-sh-auth": localStorage.getItem("token"),
              // Add any other headers needed for authorization
            },
            body: JSON.stringify({
              // ...profile, // Include other profile fields if necessary
              profile_image: uploadData.data.detail,
            }),
          }
        );

        const profileUpdateData = await profileUpdateResponse.json();
        if (profileUpdateData.code === 200) {
          // setProfile({ ...profile, profile_image: uploadData.data.detail });
          setProfile({ ...profile, profile_image: uploadData.data.detail });
          setImagePreview(uploadData.data.detail); // Update image preview with the new image
          setSelectedImage(null); // Reset selected image to hide the SUBMIT button
          toast.info("Profile updated successfully");
        } else {
          toast.error("Failed to update profile");
        }
      } else {
        alert("Failed to upload image");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setLoading(false);
    }
  };

  //calculate age using profile.dob
  const calculate_age = (dob1) => {
    var today = new Date();
    var birthDate = new Date(dob1); // create a date object directly from `dob1` argument
    var age_now = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age_now--;
    }
    return age_now;
  };

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const fetchProfile = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/me`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-sh-auth": token,
          },
        }
      );
      const data = await response.json();
      setLoading(false);
      if (data.code === 200) {
        setProfile(data.detail);
        setImagePreview(data.detail.profile_image);
      }
    };
    fetchProfile();
  }, []);

  if (loading)
    return (
      <section className="flex">
        <SidePanel />
        <div className="w-full xl:pl-[20%] lg:pl-10 md:pl-6 sm:pl-4 pl-2 pt-[6rem] px-4">
          <div className="h-screen flex items-center justify-center">
            <Spinner props="Loading" size={"80"} />
          </div>
        </div>
      </section>
    );

  return (
    // <section className="flex max-w-[1200px] mx-auto">
    <section className="flex mx-auto px-4 sm:px-2">
      <SidePanel />
      <div className="w-full xl:pl-[20%] lg:pl-10 md:pl-6 sm:pl-4 pl-2 pt-[6rem] px-4 pb-4">
        <h3 className="mb-5 text-base md:text-lg">
          <Link to="/dashboard/card_generation" className="hover:underline">
            Card Generations
          </Link>
          {">"} <span className="text-primary">User Profile</span>
        </h3>
        <div className="space-y-10">
          <div className="bg-[#F1F1F1] p-6 md:p-10 rounded-lg shadow-md">
            <div className="flex flex-col items-center justify-between mb-6 md:flex-row">
              <h1 className="mb-4 text-2xl md:text-3xl text-secondary font-semibold md:mb-0">
                Display Information
              </h1>
              <div className="flex items-center gap-x-2 text-primary">
                <Link
                  to="/dashboard/profile/edit"
                  className="flex items-center gap-x-2"
                >
                  <img src={Edit_Icon} alt="Edit Icon" className="w-6 h-6" />
                  <h4 className="text-sm md:text-base">Edit</h4>
                </Link>
              </div>
            </div>

            <div className="flex flex-col items-center gap-6 md:flex-row md:gap-x-20">
              <div className="flex flex-col items-center">
                {imagePreview ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={imagePreview}
                      alt="Profile Preview"
                      className="w-32 h-32 rounded-lg md:w-52 md:h-52"
                    />
                    <div className="flex justify-center mt-2 space-x-2 w-52"></div>
                  </div>
                ) : (
                  <label
                    htmlFor="fileInput"
                    className="w-32 h-32 md:w-52 md:h-52 rounded-lg bg-[#D2D5D7] flex items-center justify-center cursor-pointer"
                  >
                    <UploadPictureIcon
                      className="w-10 h-10 opacity-50 hover:opacity-100"
                      width={60}
                      onClick={() => navigate("/dashboard/profile/edit")}
                    />
                  </label>
                )}
              </div>

              <div className="flex flex-col space-y-5 md:flex-row md:items-center md:gap-x-20 md:space-y-0">
                <div className="flex flex-col items-start space-y-2">
                  <div>
                    <h3 className="text-sm text-gray-600 font-semibold">
                      Name
                    </h3>
                    <p className="text-lg font-semibold text-secondary md:text-2xl">
                      {profile?.fullName}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-600 font-semibold">Age</h3>
                    <p className="text-lg font-semibold text-secondary md:text-2xl">
                      {!!profile?.dob ? calculate_age(profile?.dob) : "-"}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm text-gray-600 font-semibold">
                      Gender
                    </h3>
                    <p className="text-lg font-semibold text-secondary md:text-2xl">
                      {profile?.gender??'-'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-start space-y-2">
                  <h3 className="text-sm text-gray-600 font-semibold">
                    Address
                  </h3>
                  <p className="text-lg font-semibold text-secondary md:text-2xl">
                    {profile?.address??"-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="bg-[#F1F1F1] p-5 md:p-10">
          <div className="flex flex-col items-center justify-between mb-6 md:flex-row">
            <h1 className="text-2xl text-secondary font-semibold">Login Information</h1>
          </div>
          <div className="flex flex-col items-center gap-4 md:flex-row md:gap-x-20">
            <div className="flex flex-col sm:gap-y-10 gap-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-48 gap-y-5">
              <div>
                <h3 className="text-gray-600 ">Your Email ID:</h3>
                <div className="flex items-center text-lg text-secondary font-semibold md:text-2xl gap-x-2">
                  {profile?.userId?.email}
                  <img src={CheckMark} alt="Check Mark" width={25} />
                </div>
              </div>
              </div>
              <div>
                <h3 className="text-gray-600">Password: </h3>
                <h3 className="text-2xl font-semibold">
                  **************{' '}
                  <Link to="/dashboard/reset_password" className="text-sm text-blue-600 transition duration-300 ease-in-out hover:text-blue-800">
                    (Change Password)
                  </Link>
                </h3>
              </div>
            </div>
          </div>
        </div> */}
          <div className="bg-[#F1F1F1] p-6 md:p-10 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row items-center justify-between mb-6">
              <h1 className="text-2xl md:text-3xl text-secondary font-semibold">
                Login Information
              </h1>
            </div>
            <div className="flex flex-col md:flex-row items-start gap-6 md:gap-20">
              <div className="flex flex-col gap-5 md:gap-8">
                <div className="flex flex-col gap-y-4">
                  <h3 className="text-gray-600 text-lg md:text-xl">
                    Your Email ID:
                  </h3>
                  <div className="flex items-center text-lg md:text-2xl text-secondary font-semibold gap-x-2">
                    <span>{profile?.userId?.email}</span>
                    <img
                      src={CheckMark}
                      alt="Verified"
                      width={20}
                      className="inline-block"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-y-4">
                  <h3 className="text-gray-600 text-lg md:text-xl">
                    Password:
                  </h3>
                  <div className="flex items-center text-xl md:text-2xl font-semibold">
                    <span>**************</span>
                    <Link
                      to="/dashboard/reset_password"
                      className="ml-3 text-sm text-blue-600 hover:text-blue-800 transition-colors duration-300"
                    >
                      (Change Password)
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#F1F1F1] p-6 md:p-10 rounded-lg shadow-md">
            <div className="flex flex-col md:flex-row items-center justify-between mb-6">
              <h1 className="text-2xl md:text-3xl text-secondary font-semibold">
                Payment Information
              </h1>
              <button
                onClick={() => navigate("/dashboard/premium_plan")} // Adjust the path as needed
                className="mt-4 md:mt-0 px-6 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-dark transition duration-300"
              >
                Manage Payments
              </button>
            </div>
          </div>

          {/* <div className="bg-[#F1F1F1] p-5 md:p-10">
          <div className="flex flex-col items-center justify-between mb-6 md:flex-row">
            <h1 className="text-2xl text-secondary font-semibold">
              Payment Information
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard/premium_plan")} // Adjust the path as needed
            className="px-4 py-2 bg-primary text-white font-bold rounded hover:bg-primary-dark transition duration-300"
          >
            Manage Payments
          </button>
         
        </div> */}
        </div>
      </div>
    </section>
  );
};

export default Profile;
{
  /* <div className="flex flex-col items-center gap-4 md:flex-row md:gap-x-20">
            <div className="flex flex-col sm:gap-y-10 gap-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-48 gap-y-5">
              <div>
                <h3>Your Email ID:</h3>
                <div className="flex items-center text-lg font-semibold md:text-2xl gap-x-2">
                  {profile?.userId?.email}
                  <img src={CheckMark} alt="Check Mark" width={25} />
                </div>
              </div>
              </div>
              <div>
                <h3>Password: </h3>
                <h3 className="text-2xl font-semibold">
                  **************{' '}
                  <Link to="/dashboard/reset_password" className="text-sm text-blue-600 transition duration-300 ease-in-out hover:text-blue-800">
                    (Change Password)
                  </Link>
                </h3>
              </div>
            </div>
          </div> */
}
